import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { getCreditStatus } from '../api/billing'
import useManagerStore from '../store/managerStore'

const BILLING_STATUS_REFRESH_MS = 60 * 1000

function shouldReportBillingStatusError(error) {
  const status = error?.response?.status
  return !status || status >= 500
}

export function useBillingStatusSync() {
  const tenantId = useManagerStore((s) => s.tenantId)
  const setCreditStatus = useManagerStore((s) => s.setCreditStatus)

  useEffect(() => {
    if (!tenantId) {
      setCreditStatus(null)
      return undefined
    }

    let cancelled = false

    async function loadBillingStatus(source) {
      try {
        const status = await getCreditStatus(tenantId)
        if (!cancelled) setCreditStatus(status)
      } catch (error) {
        if (cancelled || !shouldReportBillingStatusError(error)) return
        Sentry.addBreadcrumb({
          message: 'Billing status sync failed',
          level: 'warning',
          data: {
            tenantId,
            source,
            status: error?.response?.status,
          },
        })
      }
    }

    loadBillingStatus('mount')
    const interval = setInterval(
      () => loadBillingStatus('interval'),
      BILLING_STATUS_REFRESH_MS
    )

    function refreshWhenVisible(source) {
      if (document.visibilityState && document.visibilityState !== 'visible') return
      loadBillingStatus(source)
    }

    function handleVisibilityChange() {
      refreshWhenVisible('visibility')
    }

    function handleFocus() {
      refreshWhenVisible('focus')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [tenantId, setCreditStatus])
}
